import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  supabase,
  handleSupabaseError,
  createSignedStorageUrl,
  getSignedUrlRefreshDelay,
  ADMIN_AVATAR_BUCKET,
} from '@/lib';
import { ADMIN_USER_QUERY_KEYS } from '@/constants';
import type { AdminUser } from '@/types';

// Returns the admin_users row matching the currently signed-in auth session.
// Used by the persistent header to render the admin's name + email (+ avatar) and
// by any other UI that needs to know who is logged in. Cached via React Query so
// it doesn't refetch on every render; cleared on sign-out by the shared
// queryClient.clear() in useAuth.signOut().
//
// The avatar is signed fresh here (never read from the PIN vault, which reuses
// stale display fields on token refresh) and refreshed before the signed URL
// lapses.
export function useCurrentAdmin() {
  const query = useQuery({
    queryKey: [ADMIN_USER_QUERY_KEYS.CURRENT],
    queryFn: async (): Promise<AdminUser | null> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) return null;

      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) {
        handleSupabaseError(error, { operation: 'fetching current admin user' });
      }

      const admin = (data as AdminUser | null) ?? null;
      if (!admin) return null;

      if (admin.profile_photo_path) {
        const signed = await createSignedStorageUrl(ADMIN_AVATAR_BUCKET, admin.profile_photo_path);
        admin.profile_photo_url = signed.signedUrl;
        admin.profile_photo_url_expires_at = signed.expiresAt;
      }

      return admin;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Refresh the signed avatar URL shortly before it expires.
  const { data, refetch } = query;
  useEffect(() => {
    const delay = getSignedUrlRefreshDelay(data?.profile_photo_url_expires_at);
    if (delay === null) return;
    const timer = setTimeout(() => refetch(), delay);
    return () => clearTimeout(timer);
  }, [data?.profile_photo_url_expires_at, refetch]);

  return query;
}
